import { useState } from 'react';
import type { HourWindow } from '@/store/configApi';
import { Button } from '@/components/ui';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export interface DayHours {
  weekday: number;
  closed: boolean;
  windows: HourWindow[];
}

/** "17:30" -> 1050 minutes past local midnight. */
export function parseHhMm(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** 1050 -> "17:30". Values past 1440 encode a closing time after midnight. */
export function toHhMm(minutes: number): string {
  const wrapped = minutes % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

export const emptyWeek = (): DayHours[] =>
  Array.from({ length: 7 }, (_, weekday) => ({ weekday, closed: true, windows: [] }));

export const defaultWeek = (): DayHours[] =>
  Array.from({ length: 7 }, (_, weekday) => ({
    weekday,
    closed: false,
    windows: [{ open: 11 * 60, close: 22 * 60 }]
  }));

/**
 * A closing time earlier than its opening time means the kitchen runs past
 * midnight, which the engine encodes as minutes beyond 1440. Handling that here
 * keeps the operator from having to think about it.
 */
const encodeClose = (open: number, close: number): number => (close <= open ? close + 1440 : close);

export function HoursEditor({
  value,
  onChange,
  onSave,
  saving
}: {
  value: DayHours[];
  onChange: (next: DayHours[]) => void;
  onSave?: () => void;
  saving?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);

  const updateDay = (weekday: number, patch: Partial<DayHours>) => {
    onChange(value.map((day) => (day.weekday === weekday ? { ...day, ...patch } : day)));
  };

  const updateWindow = (
    weekday: number,
    index: number,
    field: 'open' | 'close',
    raw: string
  ) => {
    const minutes = parseHhMm(raw);
    if (minutes === null) {
      setError('Times need to look like 17:30.');
      return;
    }
    setError(null);

    const day = value.find((entry) => entry.weekday === weekday);
    if (!day) return;

    const windows = day.windows.map((window, windowIndex) => {
      if (windowIndex !== index) return window;
      const next = { ...window, [field]: minutes };
      return { open: next.open, close: encodeClose(next.open, next.close % 1440) };
    });

    updateDay(weekday, { windows });
  };

  /** Copies Monday across the week — the common case for a fixed schedule. */
  const copyMondayToAll = () => {
    const monday = value.find((day) => day.weekday === 1);
    if (!monday) return;
    onChange(
      value.map((day) => ({
        ...day,
        closed: monday.closed,
        windows: monday.windows.map((window) => ({ ...window }))
      }))
    );
  };

  return (
    <div className="space-y-3">
      <ul className="divide-y divide-rule border-y border-rule">
        {value.map((day) => (
          <li key={day.weekday} className="flex flex-wrap items-center gap-3 py-3">
            <span className="w-24 shrink-0 text-small text-ink">{DAYS[day.weekday]}</span>

            <label className="flex items-center gap-2 text-small text-slate">
              <input
                type="checkbox"
                checked={!day.closed}
                onChange={(event) =>
                  updateDay(day.weekday, {
                    closed: !event.target.checked,
                    windows:
                      event.target.checked && day.windows.length === 0
                        ? [{ open: 11 * 60, close: 22 * 60 }]
                        : day.windows
                  })
                }
                className="h-4 w-4 accent-[#0B7A5A]"
              />
              Open
            </label>

            {day.closed ? (
              <span className="text-small text-slate">Closed all day</span>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {day.windows.map((window, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <input
                      type="time"
                      value={toHhMm(window.open)}
                      onChange={(event) =>
                        updateWindow(day.weekday, index, 'open', event.target.value)
                      }
                      className="tabular rounded border border-rule bg-white px-2 py-1 text-small"
                      aria-label={`${DAYS[day.weekday]} opening time`}
                    />
                    <span className="text-small text-slate">to</span>
                    <input
                      type="time"
                      value={toHhMm(window.close)}
                      onChange={(event) =>
                        updateWindow(day.weekday, index, 'close', event.target.value)
                      }
                      className="tabular rounded border border-rule bg-white px-2 py-1 text-small"
                      aria-label={`${DAYS[day.weekday]} closing time`}
                    />
                    {window.close > 1440 ? (
                      <span className="text-micro text-slate">next day</span>
                    ) : null}
                    {day.windows.length > 1 ? (
                      <button
                        onClick={() =>
                          updateDay(day.weekday, {
                            windows: day.windows.filter((_, i) => i !== index)
                          })
                        }
                        className="text-micro text-slate hover:text-rust"
                        aria-label="Remove this period"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                ))}

                {day.windows.length < 3 ? (
                  <button
                    onClick={() =>
                      updateDay(day.weekday, {
                        windows: [...day.windows, { open: 17 * 60, close: 22 * 60 }]
                      })
                    }
                    className="text-small text-jade underline underline-offset-2"
                  >
                    Add a second period
                  </button>
                ) : null}
              </div>
            )}
          </li>
        ))}
      </ul>

      {error ? (
        <p role="alert" className="text-small text-rust">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <button onClick={copyMondayToAll} className="text-small text-jade underline underline-offset-2">
          Use Monday for every day
        </button>
        {onSave ? (
          <Button className="ml-auto" loading={saving} onClick={onSave}>
            Save hours
          </Button>
        ) : null}
      </div>
    </div>
  );
}
